import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import rateService from "../../services/rateService";
import stoneRateService from "../../services/stoneRateService";
import marketRateService from "../../services/marketRateService";

// ===== METAL RATE THUNKS =====
export const setRateThunk = createAsyncThunk(
  "rate/setRate",
  async (rateData, { rejectWithValue }) => {
    try {
      const response = await rateService.setRate(rateData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const setManualRateWithChargesThunk = createAsyncThunk(
  "rate/setManualRateWithCharges",
  async (rateData, { rejectWithValue }) => {
    try {
      const response = await rateService.setManualRateWithCharges(rateData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getRateDashboardThunk = createAsyncThunk(
  "rate/getRateDashboard",
  async (params, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        params?.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await rateService.getRateDashboard({
        ...params,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const approveRateThunk = createAsyncThunk(
  "rate/approveRate",
  async ({ rateId, approvalData }, { rejectWithValue }) => {
    try {
      const response = await rateService.approveRate(rateId, approvalData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getRateAnalyticsThunk = createAsyncThunk(
  "rate/getRateAnalytics",
  async (params, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        params?.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await rateService.getRateAnalytics({
        ...params,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ===== DAILY RATE THUNKS =====
export const setDailyRateThunk = createAsyncThunk(
  "rate/setDailyRate",
  async (rateData, { rejectWithValue }) => {
    try {
      const response = await rateService.setDailyRate(rateData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchFromAPIThunk = createAsyncThunk(
  "rate/fetchFromAPI",
  async (params, { rejectWithValue }) => {
    try {
      const response = await rateService.fetchFromAPI(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const confirmAPIRatesThunk = createAsyncThunk(
  "rate/confirmAPIRates",
  async (confirmData, { rejectWithValue }) => {
    try {
      const response = await rateService.confirmAPIRates(confirmData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const scheduleRateThunk = createAsyncThunk(
  "rate/scheduleRate",
  async (scheduleData, { rejectWithValue }) => {
    try {
      const response = await rateService.scheduleRate(scheduleData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const copyPreviousDayThunk = createAsyncThunk(
  "rate/copyPreviousDay",
  async (copyData, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        copyData.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await rateService.copyPreviousDay({
        ...copyData,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getTodayRateThunk = createAsyncThunk(
  "rate/getTodayRate",
  async (params, { rejectWithValue }) => {
    try {
      const response = await rateService.getTodayRate(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const morningCheckThunk = createAsyncThunk(
  "rate/morningCheck",
  async (_, { rejectWithValue }) => {
    try {
      const response = await rateService.morningCheck();
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkUpdateRatesThunk = createAsyncThunk(
  "rate/bulkUpdateRates",
  async (updates, { rejectWithValue }) => {
    try {
      const response = await rateService.bulkUpdateRates(updates);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ===== STONE RATE THUNKS =====
export const getStoneRateThunk = createAsyncThunk(
  "rate/getStoneRate",
  async (params, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        params?.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await stoneRateService.getStoneRate({
        ...params,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const setStoneRateThunk = createAsyncThunk(
  "rate/setStoneRate",
  async (stoneRateData, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        stoneRateData.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await stoneRateService.setStoneRate({
        ...stoneRateData,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const bulkImportStoneRatesThunk = createAsyncThunk(
  "rate/bulkImportStoneRates",
  async (rates, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        rates.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await stoneRateService.bulkImportStoneRates({
        ...rates,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getDiamondMatrixThunk = createAsyncThunk(
  "rate/getDiamondMatrix",
  async (params, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        params?.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await stoneRateService.getDiamondMatrix({
        ...params,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const calculateStoneValueThunk = createAsyncThunk(
  "rate/calculateStoneValue",
  async (calculationData, { rejectWithValue }) => {
    try {
      const response = await stoneRateService.calculateStoneValue(
        calculationData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ===== MARKET RATE THUNKS =====
export const fetchMarketRatesThunk = createAsyncThunk(
  "rate/fetchMarketRates",
  async (params, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        params?.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await marketRateService.fetchMarketRates({
        ...params,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const compareWithMarketThunk = createAsyncThunk(
  "rate/compareWithMarket",
  async (params, { rejectWithValue, getState }) => {
    try {
      // Get the selected branch from state if not provided
      const state = getState();
      const branchCode =
        params?.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await marketRateService.compareWithMarket({
        ...params,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const syncFromMarketThunk = createAsyncThunk(
  "rate/syncFromMarket",
  async (syncData, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        syncData.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await marketRateService.syncFromMarket({
        ...syncData,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getMarketHistoryThunk = createAsyncThunk(
  "rate/getMarketHistory",
  async (params, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        params?.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await marketRateService.getMarketHistory({
        ...params,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const initializeBranchRatesThunk = createAsyncThunk(
  "rate/initializeBranchRates",
  async (initData, { rejectWithValue }) => {
    try {
      const response = await rateService.initializeBranchRates(initData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const syncBranchConfigThunk = createAsyncThunk(
  "rate/syncBranchConfig",
  async (configData, { rejectWithValue }) => {
    try {
      const response = await rateService.syncBranchConfig(configData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ===== BRANCH CONFIG THUNKS =====
export const configureRateModeThunk = createAsyncThunk(
  "rate/configureRateMode",
  async ({ branchCode, modeData }, { rejectWithValue }) => {
    try {
      const response = await rateService.configureRateMode(
        branchCode,
        modeData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getChargesConfigThunk = createAsyncThunk(
  "rate/getChargesConfig",
  async (params, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        params?.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await rateService.getChargesConfig({
        ...params,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const setMainBranchThunk = createAsyncThunk(
  "rate/setMainBranch",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await rateService.setMainBranch(branchCode);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const syncRatesFromMainThunk = createAsyncThunk(
  "rate/syncRatesFromMain",
  async (branchCode, { rejectWithValue }) => {
    try {
      const response = await rateService.syncRatesFromMain(branchCode);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const copyRatesThunk = createAsyncThunk(
  "rate/copyRates",
  async (copyData, { rejectWithValue }) => {
    try {
      const response = await rateService.copyRates(copyData);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ===== CHARGES CONFIG THUNKS =====
export const setGSTRateThunk = createAsyncThunk(
  "rate/setGSTRate",
  async (gstData, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        gstData.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await rateService.setGSTRate({
        ...gstData,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const setCustomChargeThunk = createAsyncThunk(
  "rate/setCustomCharge",
  async (chargeData, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        chargeData.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await rateService.setCustomCharge({
        ...chargeData,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ===== CALCULATION THUNKS =====
export const calculateProductValueThunk = createAsyncThunk(
  "rate/calculateProductValue",
  async (calculationData, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        calculationData.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await rateService.calculateProductValue({
        ...calculationData,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getAllRatesWithChargesThunk = createAsyncThunk(
  "rate/getAllRatesWithCharges",
  async (params, { rejectWithValue }) => {
    try {
      const response = await rateService.getAllRatesWithCharges(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const configureBranchInheritanceThunk = createAsyncThunk(
  "rate/configureBranchInheritance",
  async ({ branchCode, inheritanceData }, { rejectWithValue }) => {
    try {
      const response = await rateService.configureBranchInheritance(
        branchCode,
        inheritanceData
      );
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getRateStatusThunk = createAsyncThunk(
  "rate/getRateStatus",
  async (params, { rejectWithValue }) => {
    try {
      const response = await rateService.getRateStatus(params);
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const getRateHistoryThunk = createAsyncThunk(
  "rate/getRateHistory",
  async (params, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        params?.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await rateService.getRateHistory({
        ...params,
        branchCode,
      });
      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteRateThunk = createAsyncThunk(
  "rate/deleteRate",
  async ({ rateId, branchCode }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const effectiveBranchCode =
        branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await rateService.deleteRate(rateId);
      return { rateId, branchCode: effectiveBranchCode };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateRateThunk = createAsyncThunk(
  "rate/updateRate",
  async ({ rateId, updateData, branchCode }, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const effectiveBranchCode =
        branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await rateService.updateRate(rateId, updateData);
      return { ...response, branchCode: effectiveBranchCode };
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

export const exportRatesThunk = createAsyncThunk(
  "rate/exportRates",
  async (params, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const branchCode =
        params?.branchCode ||
        state.rate.ui.selectedBranch ||
        state.auth.user?.branchAccess?.primaryBranch;

      const response = await rateService.exportRates({
        ...params,
        branchCode,
      });

      // Handle blob response for CSV/Excel
      if (params.format !== "json") {
        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `rates_${params.startDate}_${params.endDate}.${
            params.format === "csv" ? "csv" : "xlsx"
          }`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      return response;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      return rejectWithValue(errorMessage);
    }
  }
);

// ADD NEW ACTION CREATORS
export const clearBranchRates = createAction("rate/clearBranchRates");
export const setAPIRates = createAction("rate/setAPIRates");

// ===== ACTIONS =====
export const clearRateError = createAction("rate/clearError");
export const clearDashboard = createAction("rate/clearDashboard");
export const clearMarketData = createAction("rate/clearMarketData");
export const clearStoneRates = createAction("rate/clearStoneRates");
export const clearCalculation = createAction("rate/clearCalculation");
export const resetRateState = createAction("rate/resetState");
export const setSelectedDate = createAction("rate/setSelectedDate");
export const setSelectedBranch = createAction("rate/setSelectedBranch");
